import { API_URL } from "../config/config";
import { RequestFn } from "../api/useApi";
import { getBody } from "../helpers/body.helper";
import { toError } from "../helpers/error.helper";
import { buildQuery } from "../helpers/query.helper";
import { IDropdownOption } from "../interfaces/iDropdown.interface";
import { IBlogCard } from "../interfaces/blog/iBlogCard.interface";
import { IBlogManage } from "../interfaces/blog/iBlogManage.interface";
import { BlogQueryVM } from "../interfaces/blog/iBlogQuery.interface";
import { IBlogView } from "../interfaces/blog/iBlogView.interface";

const API_NAME = "blog";

type BlogListResponse = {
  items: IBlogCard[];
  totalCount: number;
};

export const blogService = {
  getAll,
  getAllAdmin,
  getBySlug,
  getPreview,
  getManage,
  post,
  put,
  getCategories,
};

async function getAll(query: BlogQueryVM): Promise<BlogListResponse> {
  const qs = buildQuery(query);
  const res = await fetch(`${API_URL}/${API_NAME}/GetAll?${qs}`, { method: "GET" });
  if (!res.ok) throw await toError(res, "Failed to fetch blog posts");
  return res.json();
}

async function getAllAdmin(request: RequestFn, query: BlogQueryVM): Promise<BlogListResponse> {
  const qs = buildQuery(query);
  const res = await request(`${API_URL}/${API_NAME}/GetAllAdmin?${qs}`, { method: "GET" });
  if (!res.ok) throw await toError(res, "Failed to fetch admin blog posts");
  return res.json();
}

async function getBySlug(slug: string): Promise<IBlogView> {
  const res = await fetch(`${API_URL}/${API_NAME}/GetBySlug?slug=${encodeURIComponent(slug)}`, { method: "GET" });
  if (!res.ok) throw await toError(res, "Failed to fetch blog post");
  return res.json();
}

async function getPreview(id: number, request: RequestFn): Promise<IBlogView> {
  const res = await request(`${API_URL}/${API_NAME}/Get?id=${id}`, { method: "GET" });
  if (!res.ok) throw await toError(res, "Failed to fetch blog preview");
  return res.json();
}

async function getManage(id: number, request: RequestFn): Promise<IBlogManage> {
  const res = await request(`${API_URL}/${API_NAME}/GetManage?id=${id}`, { method: "GET" });
  if (!res.ok) throw await toError(res, "Failed to fetch blog post");
  return res.json();
}

async function post(model: IBlogManage, request: RequestFn) {
  const res = await request(`${API_URL}/${API_NAME}/Create`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: getBody(model),
  });
  if (!res.ok) throw await toError(res, "Failed to create blog post");
  try {
    return await res.json();
  } catch {
    return null;
  }
}

async function put(model: IBlogManage, request: RequestFn) {
  const res = await request(`${API_URL}/${API_NAME}/Update`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: getBody(model),
  });
  if (!res.ok) throw await toError(res, "Failed to update blog post");
  try {
    return await res.json();
  } catch {
    return null;
  }
}

async function getCategories(): Promise<IDropdownOption[]> {
  const res = await fetch(`${API_URL}/blogCategory/BlogCategoriesDropdown`, { method: "GET" });
  if (!res.ok) throw await toError(res, "Failed to fetch blog categories");
  return res.json();
}
